import { snack } from '@innovamat/glimmer-components';
import {
  useSaveSubscriptionsByEmailMutation,
  useSubscriptionsByEmailQuery,
} from '@innovamat/glow-api-client';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Newsletter = {
  value: number;
  active: boolean;
  name: string;
  description: string;
};

type NewsletterGroup = {
  groupKey: string;
  groupTitle: string;
  newsletters: Newsletter[];
};

type SubscriptionConfig = {
  name: string;
  description: string;
  group: string;
  linked?: number[];
};

type UseEmailPreferencesProps = {
  email: string;
};

type useEmailPreferencesValues = {
  groupedSubscriptions: NewsletterGroup[];
  hasSomethingChanged: boolean;
  isEverythingUnsubscribed: boolean;
  isLoading: boolean;
  isSaving: boolean;
  toggleSubscription: (value: number) => void;
  unsubscribeAll: () => void;
  resetValues: () => void;
  handleSave: () => void;
};

const mapValueToSubscription: {
  [key: number]: SubscriptionConfig;
} = {
  163539: {
    name: 'settings.emailing.latest_innovamat.title',
    description: 'settings.emailing.latest_innovamat.subtitle',
    group: 'resources',
    linked: [163540],
  },
  163543: {
    name: 'settings.emailing.recommended_content.title',
    description: 'settings.emailing.recommended_content.subtitle',
    group: 'resources',
  },
  163541: {
    name: 'settings.emailing.training_events.title',
    description: 'settings.emailing.training_events.subtitle',
    group: 'training',
  },
  163542: {
    name: 'settings.emailing.expert_events.title',
    description: 'settings.emailing.expert_events.subtitle',
    group: 'training',
  },
  163544: {
    name: 'settings.emailing.family_content.title',
    description: 'settings.emailing.family_content.subtitle',
    group: 'families',
  },
  163545: {
    name: 'settings.emailing.family_products.title',
    description: 'settings.emailing.family_products.subtitle',
    group: 'families',
  },
  163546: {
    name: 'settings.emailing.family_events.title',
    description: 'settings.emailing.family_events.subtitle',
    group: 'families',
  },
};

type GroupConfig = {
  title: string;
};

const groupsConfig: { [key: string]: GroupConfig } = {
  resources: {
    title: 'settings.emailing.groups.resources.title',
  },
  training: {
    title: 'settings.emailing.groups.training.title',
  },
  families: {
    title: 'settings.emailing.groups.families.title',
  },
};

const groupSubscriptions = (subscriptions: Newsletter[]): NewsletterGroup[] => {
  const groups: { [key: string]: Newsletter[] } = {};

  subscriptions.forEach((subscription) => {
    const subscriptionConfig = mapValueToSubscription[subscription.value];
    if (subscriptionConfig?.group) {
      if (!groups[subscriptionConfig.group]) {
        groups[subscriptionConfig.group] = [];
      }
      groups[subscriptionConfig.group].push(subscription);
    }
  });

  return Object.keys(groups).map((groupKey) => ({
    groupKey,
    groupTitle:
      groupsConfig[groupKey as keyof typeof groupsConfig]?.title || '',
    newsletters: groups[groupKey],
  }));
};

export function useEmailPreferences({
  email,
}: UseEmailPreferencesProps): useEmailPreferencesValues {
  const [subscriptions, setSubscriptions] = useState<Newsletter[]>([]);
  const [initialSubscriptions, setInitialSubscriptions] = useState<
    Newsletter[]
  >([]);
  const { t } = useTranslation();
  const { data: emailSubscriptionsData, isLoading } =
    useSubscriptionsByEmailQuery({ email }, { enabled: !!email });

  const saveSubscriptionsMutation = useSaveSubscriptionsByEmailMutation({
    onSuccess: () => {
      setInitialSubscriptions([...subscriptions]);
    },
    onError: (error) => {
      console.error('Error guardando subscripciones:', error);
    },
  });

  useEffect(() => {
    if (emailSubscriptionsData?.subscriptionsByEmail) {
      const { subscribedList, unsubscribedList } =
        emailSubscriptionsData.subscriptionsByEmail;

      const mappedValues = Object.keys(mapValueToSubscription).map(Number);
      const linkedValues = Object.values(mapValueToSubscription).flatMap(
        (config) => config.linked || []
      );

      const allPossibleValues = [
        ...new Set([...mappedValues, ...linkedValues]),
      ];

      const initialSubs = allPossibleValues.map((value) => ({
        value,
        active:
          subscribedList?.includes(value) ||
          (!unsubscribedList?.includes(value) && true),
        name: mapValueToSubscription[value]?.name || '',
        description: mapValueToSubscription[value]?.description || '',
      }));

      setInitialSubscriptions(initialSubs);
      setSubscriptions(initialSubs);
    }
  }, [emailSubscriptionsData]);

  const toggleSubscription = (value: number): void => {
    const updatedSubscriptions = subscriptions.map((subscription) => {
      if (subscription.value === value) {
        return { ...subscription, active: !subscription.active };
      }
      return subscription;
    });

    const subscriptionConfig = mapValueToSubscription[value];
    const linkedCodes = subscriptionConfig?.linked;

    if (linkedCodes && linkedCodes.length > 0) {
      const targetActive = !subscriptions.find((s) => s.value === value)
        ?.active;

      linkedCodes.forEach((linkedCode) => {
        const linkedIndex = updatedSubscriptions.findIndex(
          (s) => s.value === linkedCode
        );
        if (linkedIndex !== -1) {
          updatedSubscriptions[linkedIndex] = {
            ...updatedSubscriptions[linkedIndex],
            active: targetActive,
          };
        }
      });
    }

    setSubscriptions(updatedSubscriptions);
  };

  const hasSomethingChanged = useMemo(() => {
    return (
      JSON.stringify(initialSubscriptions) !== JSON.stringify(subscriptions)
    );
  }, [initialSubscriptions, subscriptions]);

  const isEverythingUnsubscribed = useMemo(() => {
    return subscriptions.every((subscription) => !subscription.active);
  }, [subscriptions]);

  const groupedSubscriptions = useMemo(() => {
    return groupSubscriptions(subscriptions);
  }, [subscriptions]);

  const unsubscribeAll = (): void => {
    const updatedSubscriptions = subscriptions.map((subscription) => ({
      ...subscription,
      active: false,
    }));
    setSubscriptions(updatedSubscriptions);
  };

  const resetValues = (): void => {
    setSubscriptions(initialSubscriptions);
  };

  const handleSave = (): void => {
    if (!hasSomethingChanged || saveSubscriptionsMutation.isPending) {
      return;
    }

    const subscribedList = subscriptions
      .filter((sub) => sub.active)
      .map((sub) => sub.value);

    const unsubscribedList = subscriptions
      .filter((sub) => !sub.active)
      .map((sub) => sub.value);

    saveSubscriptionsMutation.mutate(
      {
        email,
        subscribedList,
        unsubscribedList,
      },
      {
        onSuccess: () => {
          snack.success(t('settings.emailing.saved'));
        },
      }
    );
  };

  return {
    groupedSubscriptions,
    hasSomethingChanged,
    isEverythingUnsubscribed,
    isLoading,
    isSaving: saveSubscriptionsMutation.isPending,
    toggleSubscription,
    unsubscribeAll,
    resetValues,
    handleSave,
  };
}
